import { AnchorLineDrawingPropertyDto } from 'models/mooring';
import VectorSource from 'ol/source/Vector';
import { angleToRadians, convertToDecimalDegrees, getSpreadAngleForN } from '../helpers';
import Feature from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { BuoyMappedForDrawing } from '../buoy';
import { achorLineLabelStyle, achorLineStyle } from './styles';
import { mapFeature, MapRenderOptions } from '../renderOptions';

type RenderAnchorLinesProps = {
  grid: BuoyMappedForDrawing[][];
  anchorLines: AnchorLineDrawingPropertyDto[];
  vectorSource: VectorSource;
};

const ANCHOR_LINE_LENTH = 0.0007;

export const renderAnchorLines = ({ grid, anchorLines, vectorSource }: RenderAnchorLinesProps, renderOptions: MapRenderOptions) => {
  const flatGrid = grid.flat();
  const rows = grid.length;
  const columns = grid[0]?.length ?? 0;

  const anchorLinesGroupedByBuoy = anchorLines.reduce((acc, current) => {
    let lines = acc.get(current.BuoyId);

    if (!lines) {
      lines = [];
      acc.set(current.BuoyId, lines);
    }

    lines.push(current);
    return acc;
  }, new Map<number, AnchorLineDrawingPropertyDto[]>());

  for (const [buoyId, lines] of Array.from(anchorLinesGroupedByBuoy.entries())) {
    const linesConnected = lines.length;

    let i = 0;
    for (const line of lines) {
      i += 1;
      const buoy = flatGrid.find((b) => b.Id === buoyId);

      if (!buoy) continue;
      const buoyPoint = buoy.Center;

      if (!buoyPoint) continue;

      const isFirstRow = buoy.Row === 1;
      const isEndRow = buoy.Row === rows;

      const isFirstColumn = buoy.Column === 1;
      const isEndColumn = buoy.Column === columns;

      let angleDegrees = 0;
      if (isFirstRow) {
        if (isFirstColumn) {
          angleDegrees = 135;
        } else if (isEndColumn) {
          angleDegrees = 45;
        } else {
          angleDegrees = 90;
        }
      } else if (isEndRow) {
        if (isFirstColumn) {
          angleDegrees = 225;
        } else if (isEndColumn) {
          angleDegrees = 315;
        } else {
          angleDegrees = 270;
        }
      } else {
        if (isFirstColumn) {
          angleDegrees = 180;
        } else if (isEndColumn) {
          angleDegrees = 0;
        } else {
          angleDegrees = 20;
        }
      }

      const start = buoyPoint;
      let end = buoyPoint ?? [0, 0];

      if (!line.AnchorLong || !line.AnchorLat) {
        const angleOffset = getSpreadAngleForN(i, linesConnected);
        const angleRadians = angleToRadians(angleDegrees, angleOffset);

        const [x1, y1] = end;

        const a = x1 + ANCHOR_LINE_LENTH * Math.cos(angleRadians);
        const b = y1 + ANCHOR_LINE_LENTH * Math.sin(angleRadians);

        end = [a, b];
      } else {
        end = [convertToDecimalDegrees(line.AnchorLong), convertToDecimalDegrees(line.AnchorLat)];
      }
      // if (!end[0] || !end[1]) continue;

      const lineFeature = new Feature({
        geometry: new LineString([fromLonLat(start), fromLonLat(end)]),
        type: 'anchorLine',
        id: line.Id,
      });

      lineFeature.setStyle(achorLineStyle(line.IsAccident));
      vectorSource.addFeature(lineFeature);

      const labelFeature = new Feature({
        geometry: new Point(fromLonLat(end)),
        type: mapFeature.anchorLine,
        id: line.Id,
      });

      const label = line.AnalysisNo ?? '';

      labelFeature.setStyle(achorLineLabelStyle(label));
      vectorSource.addFeature(labelFeature);
    }
  }
};
